// @ts-check

import React, { useMemo } from 'react';
import TextInSvg from 'utils/svg-export/components/SVGPage/components/TextInSvg';
import format from 'date-fns/format';
import getMonth from 'date-fns/getMonth';
import addDays from 'date-fns/addDays';
import endOfMonth from 'date-fns/endOfMonth';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import startOfWeek from 'date-fns/startOfWeek';
import { useCurrentPageDate, useCurrentPageWeekStarting } from 'hooks';
import { getCalendarDayColor, getCalendarSettings } from 'constants/calendarSettings';
import { weekStartingConst } from 'constants/index';
import useTextProps from './hooks/useTextProps';
import DaySize from './DaySize';
import DayBorders from './DayBorders';

/**
 * The Day component renders a day of the week.
 *
 * @param {Object} props The props for the component.
 * @param {string} props.name The name of the day.
 * @param {number} props.x The x-coordinate of the day.
 * @param {number} props.y The y-coordinate of the day.
 * @param {string} props.color The color of the day.
 * @param {string} props.fontFamily The font family of the day.
 * @param {string} props.borderColor The border color of the day.
 * @param {Partial<Record<'top'|'right'|'bottom'|'left', string>>} props.borderColors Per-side colours, prioritizing the current month on shared edges.
 * @param {string} props.fontWeight The font weight of the day.
 * @param {string} props.fontStyle The font style of the day.
 * @param {number} props.fontSize The font size of the day.
 * @param {boolean} props.borderTop Whether to show a top border on the day.
 * @param {boolean} props.borderRight Whether to show a right border on the day.
 * @param {boolean} props.borderBottom Whether to show a bottom border on the day.
 * @param {boolean} props.borderLeft Whether to show a left border on the day.
 * @param {boolean} props.isFirstColumn Whether this cell starts a calendar row.
 * @param {boolean} props.isLastVisibleRow Whether this cell is in the month's final rendered row.
 * @param {import('actions/scene.types').Shape[]} [props.dayShapes] Shapes rendered inside the day.
 * @param {ReturnType<typeof getCalendarSettings>} props.calendarSettings Calculated calendar geometry.
 * @param {boolean} [props.showDebugGuides] Whether to draw admin-only cell guides.
 * @param {'center' | 'right' | 'left'} props.textAlign The text alignment of the day.
 *
 * @returns {React.ReactElement} The rendered day component.
 */
const Day = ({
  name,
  x,
  y,
  color,
  fontFamily,
  borderColor,
  borderColors,
  fontWeight,
  fontStyle,
  fontSize,
  borderTop,
  borderRight,
  borderBottom,
  borderLeft,
  isFirstColumn,
  isLastVisibleRow,
  dayShapes,
  calendarSettings,
  showDebugGuides,
  textAlign = 'right',
}) => {
  const dayTextProps = useTextProps({ textAlign, shape: calendarSettings.DayContentShape });
  return (
    <g transform={`translate(${x} ${y})`}>
      <DaySize calendarSettings={calendarSettings} showDebugGuides={showDebugGuides} />
      <DayBorders
        cellShape={calendarSettings.DayShape}
        color={borderColor}
        colors={borderColors}
        borderTop={borderTop}
        borderRight={borderRight}
        borderBottom={borderBottom}
        borderLeft={borderLeft}
        isFirstColumn={isFirstColumn}
        isLastRow={isLastVisibleRow}
      />
      <text
        {...dayTextProps}
        y={calendarSettings.DayContentShape.margin.top}
        fontSize={fontSize}
        fontFamily={fontFamily}
        dominantBaseline="hanging"
        fill={color}
        fontWeight={fontWeight}
        fontStyle={fontStyle}
      >
        {name}
      </text>
      {dayShapes?.map((shape, index) => {
        // eslint-disable-next-line react/no-array-index-key
        return <TextInSvg key={`${index}-${shape.text}`} shape={{ fill: color, ...shape }} />;
      })}
    </g>
  );
};

/**
 * The DayContainer component renders a container for a day.
 *
 * @param {Object} props The props for the component.
 * @param {number} props.index The index of the day.
 * @param {Date} props.day The name of the day.
 * @param {import('actions/scene.types').CalendarShape} props.shape The shape of the calendar.
 *
 * @returns {React.ReactElement|null} The rendered day container component, or null for a hidden trailing cell.
 */
const DayContainer = ({ index, day, shape }) => {
  const textAlign = useMemo(() => {
    if (shape.dayNumberTextAlign) {
      return shape.dayNumberTextAlign;
    }
    if (shape.calendarType === 2) {
      return 'center';
    }
    return 'right';
  }, [shape.calendarType, shape.dayNumberTextAlign]);
  const calendarSettings = useMemo(() => getCalendarSettings(shape), [shape]);
  const currentDate = useCurrentPageDate();
  const weekStarting = useCurrentPageWeekStarting();
  const dayOfWeek = useMemo(
    () => index % calendarSettings.NumberDaysInRowAndColumn.column,
    [calendarSettings.NumberDaysInRowAndColumn.column, index],
  );
  const isCurrentMonth = useMemo(() => getMonth(day) === currentDate?.month, [currentDate?.month, day]);
  const x = useMemo(() => {
    return (index % calendarSettings.NumberDaysInRowAndColumn.column) * calendarSettings.DayShape.width;
  }, [calendarSettings.DayShape.width, calendarSettings.NumberDaysInRowAndColumn.column, index]);
  const numberOfWeek = useMemo(
    () => Math.floor(index / calendarSettings.NumberDaysInRowAndColumn.column),
    [calendarSettings.NumberDaysInRowAndColumn.column, index],
  );
  const lastVisibleCalendarRow = useMemo(() => {
    if (shape.calendarType === 2) return 0;
    if (!currentDate) return calendarSettings.NumberDaysInRowAndColumn.row - 2;

    const monthStart = new Date(currentDate.year, currentDate.month);
    const gridStart = startOfWeek(monthStart, {
      weekStartsOn: weekStartingConst.sunday === weekStarting ? 0 : 1,
    });
    return Math.floor(
      differenceInCalendarDays(endOfMonth(monthStart), gridStart) / calendarSettings.NumberDaysInRowAndColumn.column,
    );
  }, [
    calendarSettings.NumberDaysInRowAndColumn.column,
    calendarSettings.NumberDaysInRowAndColumn.row,
    currentDate,
    shape.calendarType,
    weekStarting,
  ]);
  const y = useMemo(() => {
    return numberOfWeek * calendarSettings.DayShape.height;
  }, [calendarSettings.DayShape.height, numberOfWeek]);
  const name = format(day, 'd');
  const color = getCalendarDayColor({ shape, day, isCurrentMonth });
  const isShow = numberOfWeek <= lastVisibleCalendarRow;
  const borderColor = getMonth(day) === currentDate?.month ? shape.borderColor || color : shape.secondaryColor;
  const borderColors = useMemo(() => {
    const { column } = calendarSettings.NumberDaysInRowAndColumn;
    const { margin } = calendarSettings.DayShape;
    const columnsTouch = margin.left + margin.right === 0;
    const rowsTouch = margin.top + margin.bottom === 0;
    const currentMonthColor = shape.borderColor || shape.mainColor;
    /**
     * @param {boolean} hasNeighbor
     * @param {number} offset
     */
    const sharedColor = (hasNeighbor, offset) => {
      if (isCurrentMonth) return currentMonthColor;
      if (!hasNeighbor) return borderColor;
      const neighbor = addDays(day, offset);
      return neighbor.getMonth() === currentDate?.month && neighbor.getFullYear() === Number(currentDate?.year)
        ? currentMonthColor
        : borderColor;
    };
    return {
      top: sharedColor(rowsTouch && numberOfWeek > 0, -column),
      right: sharedColor(columnsTouch && dayOfWeek < column - 1, 1),
      bottom: sharedColor(rowsTouch && numberOfWeek < lastVisibleCalendarRow, column),
      left: sharedColor(columnsTouch && dayOfWeek > 0, -1),
    };
  }, [
    calendarSettings,
    shape.borderColor,
    shape.mainColor,
    isCurrentMonth,
    borderColor,
    day,
    currentDate,
    numberOfWeek,
    dayOfWeek,
    lastVisibleCalendarRow,
  ]);
  const fontWeight = shape.isBold ? 'bold' : 'normal';
  const fontStyle = shape.isItalic ? 'italic' : 'normal';
  const showContent = isCurrentMonth || shape.showOtherMonthDays !== false;
  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
  const dayShapes = showContent
    ? shape.days
        .find((calendarDay) => calendarDay.day === format(day, 'dd.MM.yyyy'))
        ?.shapes?.map((eventShape) =>
          isWeekend && shape.useWeekendEventColor === true
            ? { ...eventShape, fill: shape.weekendEventColor || shape.eventStyle?.fill || shape.mainColor }
            : eventShape,
        )
    : [];
  if (!isShow) return null;
  return (
    <Day
      x={x}
      y={y}
      name={showContent ? name : ''}
      color={color}
      borderTop={shape.borderTop}
      borderRight={shape.borderRight}
      borderBottom={shape.borderBottom}
      borderLeft={shape.borderLeft}
      isFirstColumn={dayOfWeek === 0}
      isLastVisibleRow={numberOfWeek === lastVisibleCalendarRow}
      fontSize={shape.dayFontSize}
      borderColor={borderColor}
      borderColors={borderColors}
      fontFamily={shape.fontFamily}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      dayShapes={dayShapes}
      textAlign={textAlign}
      calendarSettings={calendarSettings}
      showDebugGuides={shape.showGridSpacingDebugGuides === true}
    />
  );
};

export default DayContainer;
